import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { CartDrawer } from "@/components/CartDrawer";
import { PopupManager } from "@/components/PopupManager";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { VaultBumper, shouldShowVaultIntro } from "@/components/VaultBumper";
import { useVisitTracker } from "@/hooks/useVisitTracker";
import { captureRef } from "@/lib/referral";
import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import MyOrders from "@/pages/MyOrders";
import OrderDetail from "@/pages/OrderDetail";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import Vault from "@/pages/Vault";
import Reviews from "@/pages/Reviews";
import Waitlist from "@/pages/Waitlist";

function AppShell() {
  useVisitTracker();
  captureRef();
  const location = useLocation();
  const onHome = location.pathname === "/";
  const [shellLive, setShellLive] = useState(() => {
    // Non-home: no bumper hold. Home: hold chrome until VaultBumper completes (every visit).
    if (typeof window === "undefined") return true;
    if (window.location.pathname !== "/") return true;
    return !shouldShowVaultIntro();
  });

  useEffect(() => {
    if (!onHome) {
      setShellLive(true);
      return;
    }
    // Every home visit: hold chrome (and PopupManager) until VaultBumper completes.
    // Prevents email-capture Turnstile from mounting inside visibility:hidden during the intro
    // (same ~10s window as the promo delay → ParityDigital race).
    setShellLive(!shouldShowVaultIntro());
  }, [onHome]);

  const shellClass = `shell-chrome ${shellLive ? "shell-chrome--live" : "shell-chrome--holding"}`;

  return (
    <>
      {onHome && <VaultBumper onComplete={() => setShellLive(true)} />}
      <div data-testid="shell-chrome" className={shellClass}>
        <AnnouncementBanner />
        <Navbar />
      </div>
      <CartDrawer />
      <div data-testid="shell-main" className={shellClass}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/dashboard" element={<MyOrders />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/orders/:id" element={<OrderDetail />} />
          <Route path="/order/:id" element={<OrderDetail />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vault-access"
            element={
              <ProtectedRoute adminOnly>
                <Vault />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Footer />
      </div>
      {/* Popups after shellLive, but OUTSIDE shell-chrome opacity/visibility so CF
          never initializes under visibility:hidden during the holding→live transition. */}
      {shellLive ? <PopupManager /> : null}
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0B0D0C",
            border: "1px solid #00E5C055",
            borderRadius: 0,
            color: "#fff",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: "12px",
          },
        }}
      />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#050606] text-white">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
